/** App shell: hash router, PIN lock gate, header and bottom navigation. */
import { useEffect, useState } from 'react';
import { Icon } from './components/Icon';
import { NavBar, type Route } from './components/NavBar';
import { PinLock } from './components/PinLock';
import { AgendaPage } from './pages/AgendaPage';
import { ApprovalsPage } from './pages/ApprovalsPage';
import { ChatPage } from './pages/ChatPage';
import { PairingPage } from './pages/PairingPage';
import { SessionsPage } from './pages/SessionsPage';
import { SettingsPage } from './pages/SettingsPage';
import { TodoPage } from './pages/TodoPage';
import { useApp } from './state/AppState';

const ROUTES: readonly Route[] = [
  'chat',
  'approvals',
  'todo',
  'agenda',
  'sessions',
  'settings',
  'pair',
];

function parseRoute(): Route {
  try {
    const raw = window.location.hash.replace(/^#\/?/u, '').split('?')[0] ?? '';
    const candidate = raw === '' ? 'chat' : raw;
    return (ROUTES as readonly string[]).includes(candidate) ? (candidate as Route) : 'chat';
  } catch {
    return 'chat';
  }
}

export function App() {
  const { settings, paired, pinRecord, unlocked, pendingCount, lock } = useApp();
  const [route, setRoute] = useState<Route>(parseRoute);

  useEffect(() => {
    const onHashChange = (): void => setRoute(parseRoute());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const navigate = (next: Route): void => {
    if (window.location.hash !== `#/${next}`) window.location.hash = `#/${next}`;
    setRoute(next);
  };

  if (pinRecord && !unlocked) return <PinLock />;

  const effectiveRoute: Route =
    !paired && route !== 'settings' && route !== 'pair' ? 'pair' : route;

  return (
    <div className="flex h-dvh flex-col bg-[#070b16] text-slate-100">
      <header className="safe-top flex items-center justify-between px-4 pb-1 pt-3">
        <div className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-indigo-500/15 text-indigo-300 ring-1 ring-indigo-400/30">
            <Icon name="spark" className="h-4 w-4" />
          </div>
          <div>
            <h1 className="text-sm font-semibold leading-none">Luminis</h1>
            <p className="mt-0.5 text-[10px] text-slate-500">私人 AI 助理 · v0.1.0</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {pinRecord ? (
            <button
              type="button"
              onClick={lock}
              aria-label="立即锁定"
              className="rounded-full border border-white/10 bg-white/5 p-1.5 text-slate-300"
            >
              <Icon name="lock" className="h-4 w-4" />
            </button>
          ) : null}
          <span
            className={`rounded-full border px-2 py-0.5 text-[10px] ${
              settings.demoMode
                ? 'border-indigo-400/30 bg-indigo-400/10 text-indigo-200'
                : 'border-emerald-400/30 bg-emerald-400/10 text-emerald-200'
            }`}
          >
            {settings.demoMode ? 'DEMO' : 'LIVE'}
          </span>
        </div>
      </header>

      <main className="min-h-0 flex-1">
        {effectiveRoute === 'chat' ? <ChatPage onNavigate={navigate} /> : null}
        {effectiveRoute === 'approvals' ? <ApprovalsPage onNavigate={navigate} /> : null}
        {effectiveRoute === 'todo' ? <TodoPage /> : null}
        {effectiveRoute === 'agenda' ? <AgendaPage /> : null}
        {effectiveRoute === 'sessions' ? <SessionsPage /> : null}
        {effectiveRoute === 'settings' ? <SettingsPage /> : null}
        {effectiveRoute === 'pair' ? (
          <PairingPage onPaired={() => navigate('chat')} onNavigate={navigate} />
        ) : null}
      </main>

      <NavBar route={effectiveRoute} pendingCount={pendingCount} onNavigate={navigate} />
    </div>
  );
}
